import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import useTranslation from "../../../../hooks/useTranslation";

interface Props {
    file: string | null;
    // Filename for the downloaded document. Matters here because the anchor points at a
    // blob object URL, which otherwise yields a browser-generated random name.
    fileName?: string;
}

export default function DownloadButton({ file, fileName }: Props) {
    const { t } = useTranslation();
    const [url, setUrl] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        let created: string | null = null;

        if (!file) {
            setUrl(null);
            return undefined;
        }

        (async () => {
            try {
                const res = await fetch(file);
                if (!res.ok) throw new Error(`Download failed with status ${res.status}`);
                const blob = await res.blob();
                if (cancelled) return;
                created = URL.createObjectURL(blob);
                setUrl(created);
            } catch {
                setUrl(null);
            }
        })();

        return () => {
            cancelled = true;
            if (created) URL.revokeObjectURL(created);
        };
    }, [file]);

    const disabled = !url;
    return (
        <a
            href={url ?? undefined}
            download={fileName ?? true}
            title={t("actions.pdf.download")}
            aria-label={t("actions.pdf.download")}
            aria-disabled={disabled}
            className={[
                "flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ink no-underline! transition-colors",
                disabled ? "pointer-events-none opacity-40" : "hover:bg-surface-3",
            ].join(" ")}
        >
            <FontAwesomeIcon icon={faDownload} />
        </a>
    );
}
